import React from 'react'
import { useUpload } from '~/hooks/useUpload'
import { Upload, Progress } from 'antd'
import styled from 'styled-components'
import { UploadOutlined } from '@ant-design/icons'
import { CompressorOptionsProps, to400Image } from '~/utils'
import { OssBucket } from '~/graphqls/types'
import { MyIcon } from '../ui/MyIcon'

interface StyledUploadProps {
  height: number
}

const StyledUpload = styled(Upload)<StyledUploadProps>`
  height: ${props => props.height}px;
  .ant-upload {
    width: 100%;
    height: 100%;
    border: none;

    &.ant-upload-select-picture-card {
      margin: 0 auto;
      background: ${props => props.theme['neutral-700']};
      border-radius: 16px;
    }
  }
`

const StyledProgress = styled(Progress)`
  padding: 0 16px;
  flex: 1;
  .ant-progress-inner {
    background: ${props => props.theme['neutral-600']};
  }
  .ant-progress-bg {
    background-color: ${props => props.theme.primary};
  }
`

const PicWrapper = styled.div`
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  height: 100%;
  justify-content: center;
  align-items: center;
  display: flex;
`

const Pic = styled.img`
  max-width: 100%;
  max-height: 100%;
`

const FillPic = styled.img`
  min-width: 100%;
  max-width: 100%;
  min-height: 100%;
  max-height: 100%;
  object-fit: cover;
`

const UploadBtn = styled.div`
  font-size: 12px;
  line-height: 20px;
  color: ${props => props.theme['neutral-400']};
`

interface PictureCardUploadProps {
  className?: string
  limit?: number
  height?: number
  label?: React.ReactNode
  icon?: React.ReactNode
  // 备注：表单控件会自动添加value属性
  value?: string
  // 备注：表单控件会自动添加onChange方法
  onChange?: (src: string) => void
  // 备注：图片上传回写时的展示效果，fill:撑满布局;responsive:自适应
  layout?: 'fill' | 'responsive'
  // 备注：默认值TRADE_PROTECT（开启原图保护），作用于作品
  bucket?: OssBucket
  /** 组件是否不允许操作，默认false */
  disabled?: boolean
  /** 是否显示进度条的百分比信息 */
  showInfo?: boolean
  /** 是否显示删除按钮 */
  showDelete?: boolean
  /** 不用onchange，新增方法变更表单值，可以不触发validate */
  onDelete?: () => void
  /** 图片尺寸 */
  getImageSize?: ({ width, height }: { width: number; height: number }) => void
  compressOptions?: CompressorOptionsProps
  isCompress?: boolean
  children?: React.ReactNode
  /** 是否检测图片违规 */
  isDetectImage?: boolean
  [key: string]: any
}

export const PictureCardUpload: React.FC<PictureCardUploadProps> = ({
  className,
  value,
  limit = 2,
  height = 120,
  label,
  icon,
  onChange,
  onDelete,
  layout = 'responsive',
  bucket = OssBucket.TRADE_PROTECT,
  disabled = false,
  showInfo = true,
  showDelete = false,
  getImageSize,
  compressOptions,
  isCompress = true,
  children,
  isDetectImage = false,
  ...restProps
}) => {
  const { loading, beforeUpload, progress, customRequest } = useUpload({
    limit,
    onUrlChange: onChange,
    bucket,
    getImageSize,
    isDetectImage,
  })

  const isIamgeUrl = (value: string) => {
    return value?.indexOf('http') > -1
  }

  return (
    <StyledUpload
      listType="picture-card"
      showUploadList={false}
      beforeUpload={beforeUpload}
      customRequest={customRequest}
      height={height}
      disabled={disabled}
      className={className}
      {...restProps}
    >
      {children ? (
        children
      ) : (
        <>
          {value && isIamgeUrl(value) && !loading ? (
            <PicWrapper>
              {layout === 'responsive' && (
                <Pic src={to400Image(value)} alt="" />
              )}
              {layout === 'fill' && <FillPic src={to400Image(value)} alt="" />}
              {showDelete && (
                <div
                  className="bg-neutral-50/50 rounded-lg absolute right-4 top-4 w-6 h-6 flex items-center justify-center"
                  onClick={e => {
                    e.stopPropagation()
                    onDelete?.()
                    getImageSize?.({ width: 0, height: 0 })
                  }}
                >
                  <MyIcon
                    icon="trash"
                    className="text-[20px] text-neutral-600"
                  />
                </div>
              )}
            </PicWrapper>
          ) : loading ? (
            <StyledProgress
              percent={progress}
              size="small"
              status="active"
              showInfo={showInfo}
            />
          ) : (
            <UploadBtn>
              {icon || (
                <UploadOutlined style={{ fontSize: 20, marginBottom: 8 }} />
              )}
              <div>{label || `支持图片格式：JPG,PNG；Max：${limit}MB`}</div>
            </UploadBtn>
          )}
        </>
      )}
    </StyledUpload>
  )
}
